/**
 * CompositionAPI - Service for user composition library management
 */

import { APIService, APIServiceError } from './APIService';
import type {
  CompositionMetadata,
  SaveCompositionRequest,
  SaveCompositionResponse,
  ListCompositionsResponse,
  GetCompositionResponse,
  CompositionUpdate,
  DuplicateCompositionResponse,
} from '../../types/api';
import type { Composition } from '../../types/composition';

/**
 * API endpoint for composition management
 * This will be configured via environment variable
 */
const COMPOSITION_API_URL =
  import.meta.env['VITE_API_COMPOSITION_URL'] ||
  'https://your-api-id.execute-api.us-west-2.amazonaws.com/prod/compositions';

/**
 * CompositionAPI service for user composition library
 */
export class CompositionAPI extends APIService {
  /**
   * Save a composition to user's library
   *
   * @param composition - Composition to save
   * @param title - Composition title
   * @returns Saved composition metadata
   * @throws APIServiceError if save fails or user is not authenticated
   */
  async save(composition: Composition, title: string): Promise<CompositionMetadata> {
    // Check authentication
    if (!this.isAuthenticated()) {
      throw new APIServiceError('You must be logged in to save compositions', 401, 'NOT_AUTHENTICATED');
    }

    // Validate inputs
    if (!composition || !composition.channels) {
      throw new APIServiceError('Invalid composition', 400, 'INVALID_COMPOSITION_DATA');
    }

    if (!title || title.trim().length === 0) {
      throw new APIServiceError('Composition title is required', 400, 'INVALID_COMPOSITION_DATA');
    }

    if (title.length > 100) {
      throw new APIServiceError('Title must be 100 characters or less', 400, 'INVALID_COMPOSITION_DATA');
    }

    const request: SaveCompositionRequest = {
      composition,
      title: title.trim(),
    };

    try {
      const response = await this.fetchWithRetry(COMPOSITION_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify(request),
        retries: 2,
        timeout: 15000,
      });

      const result: SaveCompositionResponse = await response.json();

      if (!result.composition) {
        throw new APIServiceError('Invalid response format - missing composition', 500, 'INVALID_RESPONSE');
      }

      return result.composition;
    } catch (error) {
      if (error instanceof APIServiceError) {
        // Re-throw with more specific error codes if available
        if (error.status === 401) {
          throw new APIServiceError('Authentication required. Please log in again.', 401, 'NOT_AUTHENTICATED');
        }
        if (error.status === 403 && error.code === 'COMPOSITION_LIMIT_REACHED') {
          throw error; // Pass through limit error as-is
        }
        if (error.status === 413) {
          throw new APIServiceError('Composition is too large. Maximum size is 1MB.', 413, 'COMPOSITION_TOO_LARGE');
        }
        if (error.status === 500 || error.status === 502 || error.status === 503) {
          throw new APIServiceError('Server error. Please try again in a moment.', error.status, 'S3_UPLOAD_FAILED');
        }
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new APIServiceError('Network error. Please check your connection and try again.', 0, 'NETWORK_ERROR');
      }

      throw new APIServiceError(
        `Failed to save composition: ${(error as Error).message}`,
        500,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * List all compositions for authenticated user
   *
   * @returns Array of composition metadata
   * @throws APIServiceError if list fails or user is not authenticated
   */
  async list(): Promise<CompositionMetadata[]> {
    // Check authentication
    if (!this.isAuthenticated()) {
      throw new APIServiceError('You must be logged in to view compositions', 401, 'NOT_AUTHENTICATED');
    }

    try {
      const response = await this.fetchWithRetry(COMPOSITION_API_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        retries: 2,
        timeout: 10000,
      });

      const result: ListCompositionsResponse = await response.json();

      if (!result.compositions || !Array.isArray(result.compositions)) {
        throw new APIServiceError('Invalid response format - missing compositions array', 500, 'INVALID_RESPONSE');
      }

      return result.compositions;
    } catch (error) {
      if (error instanceof APIServiceError) {
        // Re-throw with more specific error codes if available
        if (error.status === 401) {
          throw new APIServiceError('Authentication required. Please log in again.', 401, 'NOT_AUTHENTICATED');
        }
        if (error.status === 500 || error.status === 502 || error.status === 503) {
          throw new APIServiceError('Server error. Please try again in a moment.', error.status, 'DYNAMODB_ERROR');
        }
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new APIServiceError('Network error. Please check your connection and try again.', 0, 'NETWORK_ERROR');
      }

      throw new APIServiceError(
        `Failed to list compositions: ${(error as Error).message}`,
        500,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * Load a composition by ID
   *
   * @param compositionId - Composition ID
   * @returns Composition data
   * @throws APIServiceError if load fails or user is not authenticated
   */
  async load(compositionId: string): Promise<Composition> {
    // Check authentication
    if (!this.isAuthenticated()) {
      throw new APIServiceError('You must be logged in to load compositions', 401, 'NOT_AUTHENTICATED');
    }

    // Validate input
    if (!compositionId || compositionId.trim().length === 0) {
      throw new APIServiceError('Composition ID is required', 400, 'INVALID_COMPOSITION_DATA');
    }

    try {
      const url = `${COMPOSITION_API_URL}/${encodeURIComponent(compositionId)}`;

      const response = await this.fetchWithRetry(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        retries: 2,
        timeout: 10000,
      });

      const result: GetCompositionResponse = await response.json();

      if (!result.composition || !result.composition.channels) {
        throw new APIServiceError('Invalid composition format', 500, 'INVALID_RESPONSE');
      }

      return result.composition;
    } catch (error) {
      if (error instanceof APIServiceError) {
        // Re-throw with more specific error codes if available
        if (error.status === 401) {
          throw new APIServiceError('Authentication required. Please log in again.', 401, 'NOT_AUTHENTICATED');
        }
        if (error.status === 403) {
          throw new APIServiceError('You do not have permission to access this composition.', 403, 'UNAUTHORIZED_ACCESS');
        }
        if (error.status === 404) {
          throw new APIServiceError('Composition not found. It may have been deleted.', 404, 'COMPOSITION_NOT_FOUND');
        }
        if (error.status === 500 || error.status === 502 || error.status === 503) {
          throw new APIServiceError('Server error. Please try again in a moment.', error.status, 'S3_DOWNLOAD_FAILED');
        }
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new APIServiceError('Network error. Please check your connection and try again.', 0, 'NETWORK_ERROR');
      }

      throw new APIServiceError(
        `Failed to load composition: ${(error as Error).message}`,
        500,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * Update composition metadata (title, etc.)
   *
   * @param compositionId - Composition ID
   * @param updates - Updates to apply
   * @returns Updated composition metadata
   * @throws APIServiceError if update fails or user is not authenticated
   */
  async update(compositionId: string, updates: CompositionUpdate): Promise<CompositionMetadata> {
    // Check authentication
    if (!this.isAuthenticated()) {
      throw new APIServiceError('You must be logged in to update compositions', 401, 'NOT_AUTHENTICATED');
    }

    // Validate inputs
    if (!compositionId || compositionId.trim().length === 0) {
      throw new APIServiceError('Composition ID is required', 400, 'INVALID_COMPOSITION_DATA');
    }

    if (!updates || Object.keys(updates).length === 0) {
      throw new APIServiceError('No updates provided', 400, 'INVALID_COMPOSITION_DATA');
    }

    if (updates.title !== undefined) {
      if (!updates.title || updates.title.trim().length === 0) {
        throw new APIServiceError('Title cannot be empty', 400, 'INVALID_COMPOSITION_DATA');
      }

      if (updates.title.length > 100) {
        throw new APIServiceError('Title must be 100 characters or less', 400, 'INVALID_COMPOSITION_DATA');
      }
    }

    try {
      const url = `${COMPOSITION_API_URL}/${encodeURIComponent(compositionId)}`;

      const response = await this.fetchWithRetry(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify(updates),
        retries: 2,
        timeout: 15000,
      });

      const result = await response.json();

      if (!result.composition) {
        throw new APIServiceError('Invalid response format - missing composition', 500, 'INVALID_RESPONSE');
      }

      return result.composition;
    } catch (error) {
      if (error instanceof APIServiceError) {
        // Re-throw with more specific error codes if available
        if (error.status === 401) {
          throw new APIServiceError('Authentication required. Please log in again.', 401, 'NOT_AUTHENTICATED');
        }
        if (error.status === 403) {
          throw new APIServiceError('You do not have permission to update this composition.', 403, 'UNAUTHORIZED_ACCESS');
        }
        if (error.status === 404) {
          throw new APIServiceError('Composition not found. It may have been deleted.', 404, 'COMPOSITION_NOT_FOUND');
        }
        if (error.status === 500 || error.status === 502 || error.status === 503) {
          throw new APIServiceError('Server error. Please try again in a moment.', error.status, 'DYNAMODB_ERROR');
        }
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new APIServiceError('Network error. Please check your connection and try again.', 0, 'NETWORK_ERROR');
      }

      throw new APIServiceError(
        `Failed to update composition: ${(error as Error).message}`,
        500,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * Delete a composition
   *
   * @param compositionId - Composition ID
   * @throws APIServiceError if delete fails or user is not authenticated
   */
  async delete(compositionId: string): Promise<void> {
    // Check authentication
    if (!this.isAuthenticated()) {
      throw new APIServiceError('You must be logged in to delete compositions', 401, 'NOT_AUTHENTICATED');
    }

    // Validate input
    if (!compositionId || compositionId.trim().length === 0) {
      throw new APIServiceError('Composition ID is required', 400, 'INVALID_COMPOSITION_DATA');
    }

    try {
      const url = `${COMPOSITION_API_URL}/${encodeURIComponent(compositionId)}`;

      const response = await this.fetchWithRetry(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        retries: 2,
        timeout: 10000,
      });

      // Check if response is successful (204 No Content or 200 OK)
      if (!response.ok) {
        throw new APIServiceError('Failed to delete composition', response.status, 'DELETE_FAILED');
      }
    } catch (error) {
      if (error instanceof APIServiceError) {
        // Re-throw with more specific error codes if available
        if (error.status === 401) {
          throw new APIServiceError('Authentication required. Please log in again.', 401, 'NOT_AUTHENTICATED');
        }
        if (error.status === 403) {
          throw new APIServiceError('You do not have permission to delete this composition.', 403, 'UNAUTHORIZED_ACCESS');
        }
        if (error.status === 404) {
          throw new APIServiceError('Composition not found. It may have already been deleted.', 404, 'COMPOSITION_NOT_FOUND');
        }
        if (error.status === 500 || error.status === 502 || error.status === 503) {
          throw new APIServiceError('Server error. Please try again in a moment.', error.status, 'S3_DOWNLOAD_FAILED');
        }
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new APIServiceError('Network error. Please check your connection and try again.', 0, 'NETWORK_ERROR');
      }

      throw new APIServiceError(
        `Failed to delete composition: ${(error as Error).message}`,
        500,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * Duplicate a composition
   *
   * @param compositionId - Composition ID to duplicate
   * @returns New composition metadata
   * @throws APIServiceError if duplicate fails or user is not authenticated
   */
  async duplicate(compositionId: string): Promise<CompositionMetadata> {
    // Check authentication
    if (!this.isAuthenticated()) {
      throw new APIServiceError('You must be logged in to duplicate compositions', 401, 'NOT_AUTHENTICATED');
    }

    // Validate input
    if (!compositionId || compositionId.trim().length === 0) {
      throw new APIServiceError('Composition ID is required', 400, 'INVALID_COMPOSITION_DATA');
    }

    try {
      const url = `${COMPOSITION_API_URL}/${encodeURIComponent(compositionId)}/duplicate`;

      const response = await this.fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        retries: 2,
        timeout: 15000,
      });

      const result: DuplicateCompositionResponse = await response.json();

      if (!result.metadata) {
        throw new APIServiceError('Invalid response format - missing metadata', 500, 'INVALID_RESPONSE');
      }

      return result.metadata;
    } catch (error) {
      if (error instanceof APIServiceError) {
        // Re-throw with more specific error codes if available
        if (error.status === 401) {
          throw new APIServiceError('Authentication required. Please log in again.', 401, 'NOT_AUTHENTICATED');
        }
        if (error.status === 403) {
          if (error.code === 'COMPOSITION_LIMIT_REACHED') {
            throw error; // Pass through limit error as-is
          }
          throw new APIServiceError('You do not have permission to duplicate this composition.', 403, 'UNAUTHORIZED_ACCESS');
        }
        if (error.status === 404) {
          throw new APIServiceError('Composition not found. It may have been deleted.', 404, 'COMPOSITION_NOT_FOUND');
        }
        if (error.status === 500 || error.status === 502 || error.status === 503) {
          throw new APIServiceError('Server error. Please try again in a moment.', error.status, 'S3_UPLOAD_FAILED');
        }
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new APIServiceError('Network error. Please check your connection and try again.', 0, 'NETWORK_ERROR');
      }

      throw new APIServiceError(
        `Failed to duplicate composition: ${(error as Error).message}`,
        500,
        'UNKNOWN_ERROR'
      );
    }
  }
}

// Export singleton instance
export const compositionAPI = new CompositionAPI();
